import { useEffect, useState } from "react";

import { useRef } from "react";
import { checkIf } from "../../utils";
import { Box, Input, InputAdornment, TextField, Typography } from "@mui/material";
import { PinIcon } from "../../assets/icons";

interface IProps {
  onPlaceSelected: (place: google.maps.Place) => void;
  floatingLayer?: boolean;
}

export default function MapsSearch(props: IProps) {
  const resultsContainerRef = useRef<HTMLElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  let resultsContainerElement: HTMLElement;

  let newestRequestId = 0;

  // Add an initial request body.
  const request = {
    input: "",
    language: "en-US",
    includedRegionCodes: ["qa"],
  };

  function init() {
    resultsContainerElement = resultsContainerRef.current as HTMLElement;
    inputRef.current?.addEventListener("input", makeAutocompleteRequest);
    refreshToken(request);
  }

  async function makeAutocompleteRequest(inputEvent: Event) {
    // Reset elements and exit if an empty string is received.
    if ((inputEvent.target as HTMLInputElement).value == "") {
      resultsContainerElement?.replaceChildren();
      return;
    }

    // Add the latest char sequence to the request.
    request.input = (inputEvent.target as HTMLInputElement).value;

    // To avoid race conditions, store the request ID and compare after the request.
    const requestId = ++newestRequestId;

    // Fetch autocomplete suggestions and show them in a list.
    // @ts-ignore
    const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

    // If the request has been superseded by a newer request, do not render the output.
    if (requestId !== newestRequestId) return;

    // Clear the list first.
    resultsContainerElement.replaceChildren();

    for (const suggestion of suggestions) {
      const placePrediction = suggestion.placePrediction;

      // Create a link for the place, add an event handler to fetch the place.
      const a = document.createElement("a");
      a.addEventListener("click", () => {
        onPlaceSelected(placePrediction!.toPlace());
      });
      a.innerText = placePrediction!.text.toString();

      // Create a new list item element.
      const li = document.createElement("li");
      li.appendChild(a);
      resultsContainerElement.appendChild(li);
    }
  }

  // Event handler for clicking on a suggested place.
  async function onPlaceSelected(place: any) {
    await place.fetchFields({
      fields: ["displayName", "formattedAddress", "location"],
    });
    const placeText = document.createTextNode(`${place.displayName}: ${place.formattedAddress}`);
    resultsContainerElement.replaceChildren();
    inputRef.current!.value = `${place.formattedAddress}`;
    refreshToken(request);
    props.onPlaceSelected(place);
  }

  // Helper function to refresh the session token.
  function refreshToken(request: any) {
    // Create a new session token and add it to the request.
    request.sessionToken = new google.maps.places.AutocompleteSessionToken();
  }

  useEffect(() => {
    if (resultsContainerRef.current && inputRef.current) {
      init();
    }
  }, [resultsContainerRef, inputRef]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          borderWidth: "1px",
          borderColor: "text.fontLight2",
          borderStyle: "solid",
          borderRadius: "12px",
          padding: "14px 16px 14px 40px",
          width: "100%",
          position: "relative",
          backgroundColor: "text.white",
        }}
      >
        <input
          style={{
            width: "100%",
            border: "none",
            outline: "none",
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: "24px",
            color: "#18191B",
          }}
          ref={inputRef}
          id="input"
          type="text"
          placeholder="Search for a place..."
        />
        <Typography
          sx={{
            position: "absolute",
            left: "16px",
            top: "16px",
            color: "text.fontDark",
            display: "flex",
            alignItems: "center",
          }}
        >
          <PinIcon />
        </Typography>
      </Box>
      <Box
        ref={resultsContainerRef}
        id="results"
        sx={{
          backgroundColor: "text.white",
          width: "100%",
          "& li": {
            listStyle: "none",
            margin: "0px",
            borderBottomWidth: "1px",
            borderBottomStyle: "solid",
            borderBottomColor: "text.fontLight2",
            padding: "10px 6px",
            cursor: "pointer",
          },
          "& li:last-child": { borderBottom: "none" },
          "& li > a": { fontSize: "14px", fontWeight: 600, lineHeight: "normal", color: "text.fontDefault" },
          borderRadius: props.floatingLayer ? "12px" : "0px",
        }}
      ></Box>
    </Box>
  );
}
